import { cn } from "../lib/utils"

// ─── Variant: Full Page Loader ────────────────────────────────────────────────
export function PageLoader({ message = "Loading…" }: { message?: string }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-5">
        <InmeetSpinner size="lg" />
        <p className="text-sm text-muted-foreground font-medium animate-pulse">{message}</p>
      </div>
    </div>
  )
}

// ─── Variant: Section / Panel Loader (fills its container) ───────────────────
export function SectionLoader({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full min-h-[220px] gap-4">
      <InmeetSpinner size="md" />
      {message && (
        <p className="text-xs text-muted-foreground animate-pulse">{message}</p>
      )}
    </div>
  )
}

// ─── Variant: Inline / Button Loader ─────────────────────────────────────────
export function InlineLoader({ className }: { className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-current opacity-60"
          style={{ animation: `dotBounce 1.2s ease-in-out ${i * 0.2}s infinite` }}
        />
      ))}
    </span>
  )
}

// ─── Variant: Overlay Loader (over a card/modal) ──────────────────────────────
export function OverlayLoader({ message }: { message?: string }) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl bg-card/80 backdrop-blur-[2px]">
      <InmeetSpinner size="sm" />
      {message && (
        <p className="text-xs text-muted-foreground mt-3 animate-pulse">{message}</p>
      )}
    </div>
  )
}

// ─── Variant: Full Page Dots Overlay Loader ───────────────────────────────────
export function FullPageDotsLoader({ message = "Please wait..." }: { message?: string }) {
  return (
    <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-background/5 backdrop-blur-[2px]">
      <div className="flex items-center gap-2.5">
        {[0, 1, 2].map(i => (
          <span
            key={i}
            className="w-3 h-3 rounded-full bg-primary"
            style={{ animation: `dotBounce 1.2s ease-in-out ${i * 0.18}s infinite` }}
          />
        ))}
      </div>
      {message && <p className="mt-4 font-semibold text-md text-muted-foreground">{message}</p>}

      <style>{`
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0) scale(0.9); opacity: 0.45; }
          40% { transform: translateY(-6px) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}

// ─── Variant: Button Loader (inside buttons) ──────────────────────────────────
export function ButtonLoader() {
  return (
    <span className="inline-flex items-center gap-1">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1 h-1 rounded-full bg-current"
          style={{ animation: `dotBounce 1.2s ease-in-out ${i * 0.15}s infinite` }}
        />
      ))}
    </span>
  )
}

// ─── Skeleton Loaders ─────────────────────────────────────────────────────────
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-card overflow-hidden", className)}>
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="h-3 w-2/5" />
          </div>
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
        <div className="flex gap-2 pt-1">
          <Skeleton className="h-7 w-20 rounded-lg" />
          <Skeleton className="h-7 w-16 rounded-lg" />
        </div>
      </div>
    </div>
  )
}

export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="w-full space-y-0 border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-muted/30 border-b border-border">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className={`h-3 ${i === 0 ? "w-1/3" : "flex-1"}`} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, row) => (
        <div
          key={row}
          className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0"
          style={{ animationDelay: `${row * 0.05}s` }}
        >
          <div className="flex items-center gap-2 w-1/3">
            <Skeleton className="w-8 h-8 rounded-full shrink-0" />
            <Skeleton className="h-3 flex-1" />
          </div>
          {Array.from({ length: cols - 1 }).map((_, col) => (
            <Skeleton key={col} className={`h-3 flex-1 ${col === cols - 2 ? "w-16" : ""}`} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function SkeletonStatCards({ count = 4 }: { count?: number }) {
  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-border bg-card p-6 relative overflow-hidden"
          style={{ animationDelay: `${i * 0.08}s` }}
        >
          <div className="flex items-center gap-3">
            <Skeleton className="w-12 h-12 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export function SkeletonList({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card"
          style={{ animationDelay: `${i * 0.06}s` }}
        >
          <Skeleton className="w-10 h-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-2.5 w-3/5" />
          </div>
          <Skeleton className="w-16 h-7 rounded-lg shrink-0" />
        </div>
      ))}
    </div>
  )
}

// ─── Core Spinner — InMeet branded ───────────────────────────────────────────
type SpinnerSize = "xs" | "sm" | "md" | "lg" | "xl"

const SIZES: Record<SpinnerSize, { outer: string; ring: string; dot: string }> = {
  xs: { outer: "w-5 h-5",  ring: "border-2", dot: "w-1.5 h-1.5" },
  sm: { outer: "w-8 h-8",  ring: "border-2", dot: "w-2 h-2" },
  md: { outer: "w-12 h-12", ring: "border-[3px]", dot: "w-2.5 h-2.5" },
  lg: { outer: "w-16 h-16", ring: "border-[3px]", dot: "w-3 h-3" },
  xl: { outer: "w-20 h-20", ring: "border-4",   dot: "w-4 h-4" },
}

export function InmeetSpinner({
  size = "md",
  className,
}: {
  size?: SpinnerSize
  className?: string
}) {
  const s = SIZES[size]
  return (
    <div className={cn("relative flex items-center justify-center", s.outer, className)}>
      {/* Outer ring */}
      <div
        className={cn(
          "absolute inset-0 rounded-full border-primary/20",
          s.ring,
        )}
      />
      {/* Spinning arc */}
      <div
        className={cn(
          "absolute inset-0 rounded-full border-primary border-t-transparent",
          s.ring,
        )}
        style={{ animation: "spin 0.75s linear infinite" }}
      />
      {/* Pulsing center dot */}
      <div
        className={cn(
          "rounded-full bg-primary",
          s.dot,
        )}
        style={{ animation: "pulseScale 1.5s ease-in-out infinite" }}
      />

      {/* Keyframe styles */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulseScale {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50%       { transform: scale(1.3); opacity: 1; }
        }
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0);    opacity: 0.4; }
          40%            { transform: translateY(-4px); opacity: 1;   }
        }
      `}</style>
    </div>
  )
}

// ─── Base Skeleton atom ───────────────────────────────────────────────────────
function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-md bg-muted animate-pulse",
        className,
      )}
    />
  )
}
